import React from "react";
import { useFacetValues } from "@radarly/core/clause/use-cases";
import { TermCard } from "@radarly/models/cards";
import { isHiddenTermsCard } from "@radarly/models/cards/is";
import {
  Dataviz,
  isCloudDataviz,
  isHorizontalBarChartDataviz,
  updateDatavizOptions,
} from "@radarly/models/dataviz";
import { FullFacet } from "@radarly/models/facets";
import { getComparaisonPeriodFromFragments } from "@radarly/models/fragment";
import { SearchFragmentTerm } from "@radarly/models/search";
import { useFragmentsData } from "../data";
import Loading from "../loading";
import Cloud from "./cloud/inner";
import DoughnutChart from "./doughnut-chart/inner";
import HorizontalBarChart from "./horizontal-bar-chart/inner";

export interface SharedWidgetsTermProps {
  project: number;
  fragment: SearchFragmentTerm;
  evolutionFragment?: SearchFragmentTerm;
  className?: string;
  onClickItem?: (uid: string | number | Array<string | number>) => void;
  facet: FullFacet;
  forceReload?: boolean;
  isFullscreen?: boolean;
  closeFullscreen?: () => void;
  isEdition?: boolean;
  setDataviz?: (dataviz: Dataviz) => void;
  isHideable?: boolean;
  card: TermCard;
  isPNG?: boolean;
}

export default function SharedWidgetsTerm({
  project,
  fragment,
  evolutionFragment,
  onClickItem,
  forceReload,
  isFullscreen,
  closeFullscreen,
  isEdition,
  setDataviz,
  isHideable,
  card,
  facet,
  ...props
}: SharedWidgetsTermProps) {
  const { loading: isFacetValuesLoading, values } = useFacetValues(
    project,
    facet
  );
  const _facet = { ...facet, values };

  const comparisonPeriod = getComparaisonPeriodFromFragments(
    fragment,
    evolutionFragment
  );
  const { loading, data, comparaisonData } = useFragmentsData(
    project,
    fragment,
    evolutionFragment
  );
  const _onClickItem = (
    uid: string | number,
    status?: "include" | "exclude",
    label?: string
  ) => {
    if (isFullscreen) {
      closeFullscreen();
    }
    if (isEdition && isHiddenTermsCard(card)) {
      return setDataviz(
        updateDatavizOptions(card.dataviz, "hiddenTerms", {
          id: label || String(uid),
          status,
        })
      );
    }
    if (!!onClickItem) {
      onClickItem(uid);
    }
  };

  const _loading = loading || isFacetValuesLoading;
  return (
    <Loading loading={_loading} forceReload={forceReload}>
      {isHorizontalBarChartDataviz(card.dataviz) ? (
        <HorizontalBarChart
          loading={_loading}
          data={data}
          comparaisonData={comparaisonData}
          fragment={fragment}
          dataviz={card.dataviz}
          onClickGauge={_onClickItem}
          comparisonPeriod={comparisonPeriod}
          isFullscreen={isFullscreen}
          isEdition={isEdition}
          facet={_facet}
          {...props}
        />
      ) : isCloudDataviz(card.dataviz) ? (
        <Cloud
          loading={_loading}
          data={data}
          fragment={fragment}
          dataviz={card.dataviz}
          onClickItem={_onClickItem}
          isEdition={isEdition}
          facet={_facet}
          {...props}
        />
      ) : (
        <DoughnutChart
          loading={_loading}
          data={data}
          fragment={fragment}
          dataviz={card.dataviz}
          onClickPie={_onClickItem}
          onClickLegend={_onClickItem}
          isFullscreen={isFullscreen}
          facet={_facet}
          {...props}
        />
      )}
    </Loading>
  );
}
